import { Box, ScrollArea, Text } from "@salestrak/ui";

import { formatDate } from "#app/lib/utils/format-date";
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderSubtitle,
  PageHeaderTitle,
  SETTINGS_CONTENT_MAX_WIDTH,
  SettingsBackBar,
  SettingsBackBarClearance,
  SettingsPageGutter,
} from "#app/components/page-header";
import { EntityTag } from "#app/components/entity";
import { ApiKeyActionsMenu } from "./api-key-actions-menu";
import { PermissionBadges } from "./permission-badge";
import { SettingsGroup, SettingsGroupRow } from "./settings-group";
import type { ApiKeyItem } from "#app/hooks/settings/use-api-key-mutations";
import type { Omittable } from "@salestrak/ui";

type ApiKeyDetailViewProps = Omittable<{
  apiKey: ApiKeyItem;
  onBack: () => void;
  onEdit: () => void;
  onRevoke: () => void;
}>;

export function ApiKeyDetailView({
  apiKey,
  onBack,
  onEdit,
  onRevoke,
}: ApiKeyDetailViewProps) {
  const dateLabel = apiKey.creationDate ? formatDate(apiKey.creationDate) : null;
  const expiresLabel = apiKey.expires ? formatDate(apiKey.expires) : null;

  return (
    <Box direction="column" fill="flex" position="relative">
      <SettingsBackBar label="API" onClick={onBack} />
      <ScrollArea>
        <SettingsBackBarClearance />
        <PageHeader centered pinnedBack>
          <PageHeaderTitle>{apiKey.name}</PageHeaderTitle>
          <PageHeaderActions>
            <ApiKeyActionsMenu onEdit={onEdit} onRevoke={onRevoke} />
          </PageHeaderActions>
          {dateLabel ? (
            <PageHeaderSubtitle>Created {dateLabel}</PageHeaderSubtitle>
          ) : null}
        </PageHeader>
        <Box align="center" direction="column" paddingX="md" paddingY="md">
          <Box direction="column" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            {/* The title already names the key; the card answers whose it
              * is, what it may do, and how long it lasts. */}
            <SettingsGroup label="Key">
              <SettingsGroupRow label="Owner">
                <EntityTag
                  entity={{
                    kind: "user",
                    id: apiKey.user?.userId ?? apiKey.userId,
                    user: apiKey.user ?? null,
                  }}
                  size="sm"
                />
              </SettingsGroupRow>
              <SettingsGroupRow label="Permissions">
                <PermissionBadges permissions={apiKey.permissions} />
              </SettingsGroupRow>
              <SettingsGroupRow label="Expires">
                <Text size="sm" variant="muted">{expiresLabel ?? "Never"}</Text>
              </SettingsGroupRow>
            </SettingsGroup>

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}
